import isNull from 'lodash-es/isNull.js';

/***
 * Класс для ленивой инициализации блоков. Блоки будут инициализироваться после того как они будут рядом с областью видимости +- 50% от рзамера экрана
 * @example
 * Методы:
 *
 * init - функция отложенной инициализации
 * destroy - функция отменяет отложенную инициализацию
 * earlyInit - функция досрочной инициализации. Отменяет отложенную инициализацию
 *
 * Пример:
 * this.LazyInit = new window.Block.LazyInit({ ins: this, callback: this.init, addClass: 'custom-scrollbar_inited' });
 * this.LazyInit.init();
 */
class LazyInit {
  /**
   * Конструктор
   * @param {Object} params - параметры для инициализации
   * @param {Object} params.ins - инстанс блока, за которым нужно следить
   * @param {Function} params.callback - callback функция, которая будет вызвана если block попал в область видимости +- 50%
   * @param {String|undefined} params.addClass - класс, который нужно добавить после добавления слушателя
   */
  constructor({ ins, callback, addClass }) {
    this.ins = ins;
    this.targetCallback = callback;
    this.addClass = addClass;
    this.ins.block.LazyInit = this;
  }

  /***
   * Добавление ленивой инициализации
   */
  init = () => {
    LazyInit.observer.observe(this.ins.block);
    LazyInit.instances.add(this.ins);
    this.addClass && this.ins.block.classList.add(this.addClass);
  };

  /***
   * Досрочная инициализация с удалением ленивой
   */
  earlyInit = async () => {
    if (!LazyInit.instances.has(this.ins)) return;

    LazyInit.observer.unobserve(this.ins.block);
    this.addClass && this.ins.block.classList.add(this.addClass);
    await this.callback();
  };

  callback = async () => {
    await this.targetCallback();
    LazyInit.instances.delete(this.ins);
    this.ins.block.dispatchEvent(new CustomEvent('iv-lazyInit-init', { detail: this.ins, bubbles: true }));
  };

  /***
   * Удаление слушателя
   */
  destroy = () => {
    if (LazyInit.observer) LazyInit.observer.unobserve(this.ins.block);
    if (this.ins.block) delete this.ins.block.LazyInit;
  };

  /***
   * Callback для обзервера
   * @param entries {IntersectionObserverEntry[]} - все элементы, попавшие в область видимости
   * @param observer {IntersectionObserver} - объект обзервера
   */
  static observerHandler = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.LazyInit.callback();
        observer.unobserve(entry.target);
      }
    });
  };

  /***
   * Глобальный обзервер
   * @type {null|IntersectionObserver}
   */
  static _observer = null;

  /***
   * Коллекция всех инстансов для инициализации, хранится для предотвращения множественного инициализирования
   * @type {WeakSet}
   */
  static instances = new WeakSet();

  /***
   * Создание и получение обзервера
   * @return {IntersectionObserver|null}
   */
  static get observer() {
    if (isNull(LazyInit._observer)) LazyInit._observer = LazyInit.crateObserver();
    return LazyInit._observer;
  }

  /**
   * Установка обзервера
   * @param newValue {IntersectionObserver} - новый обзервер
   */
  static set observer(newValue) {
    LazyInit._observer = newValue;
  }

  /***
   * Функция создания глобального обзервера
   */
  static crateObserver() {
    const observerOptions = {
      rootMargin: '50% 0px 50% 0px',
      threshold: 0,
    };
    return new IntersectionObserver(LazyInit.observerHandler, observerOptions);
  }
}

// Экспорт;
window.Block.LazyInit = LazyInit;
export { LazyInit };
